import {
  type DraftArtifactKind,
  staleApprovalText,
  staleReasonOf,
  stalenessWindowsOf,
} from '@sigora/domain';

import type { ArtifactVersion, AudienceCatalog } from '../api/client.ts';
import { ArtifactBody } from './ArtifactBody.tsx';
import { type ApprovalState, KIND_LABELS, STATUS_LABELS, type SidebarArtifact } from './state.ts';

export interface ArtifactCardProps {
  kind: DraftArtifactKind;
  artifact: SidebarArtifact | null;
  approval: ApprovalState;
  content: unknown;
  /** The version `content` came from; absent when that cannot be established. */
  contentVersion: number | undefined;
  /** The document could not be read, which is not the same as not written yet. */
  contentUnreadable: boolean;
  /** Passed through for the audience card, which renders its filters in the panel's words. */
  audienceCatalog: AudienceCatalog | null;
  history: readonly ArtifactVersion[] | undefined;
  historyOpen: boolean;
  /** Already worded by `describeSave` or `refusalMessage`; the card only places it. */
  notice: string | null;
  busy: boolean;
  locked: boolean;
  onApprove: () => void;
  onToggleHistory: () => void;
  /** `baseVersion` is the version the editor was opened on; see `ArtifactBodyProps`. */
  onSaveDocument: (content: unknown, baseVersion: number) => void;
  /** `baseVersion` is the version the wording editor was opened on. */
  onRewordQuestion: (questionId: string, text: string, baseVersion: number) => void;
}

export const ArtifactCard = ({
  kind,
  artifact,
  approval,
  content,
  contentVersion,
  contentUnreadable,
  audienceCatalog,
  history,
  historyOpen,
  notice,
  busy,
  locked,
  onApprove,
  onToggleHistory,
  onSaveDocument,
  onRewordQuestion,
}: ArtifactCardProps) => {
  if (artifact === null) {
    return (
      <article className="artifact-card" data-test-id={`artifact-${kind}`}>
        <header>
          <h3>{KIND_LABELS[kind]}</h3>
          <span className="artifact-status" data-test-id={`artifact-status-${kind}`}>
            Not written yet
          </span>
        </header>
      </article>
    );
  }

  const stalenessWindows = stalenessWindowsOf(artifact.staleReason);

  return (
    <article className="artifact-card" data-test-id={`artifact-${kind}`}>
      <header>
        <h3>{KIND_LABELS[kind]}</h3>
        <span
          className={`artifact-status artifact-status-${artifact.status}`}
          data-test-id={`artifact-status-${kind}`}
        >
          {STATUS_LABELS[artifact.status]}
        </span>
        <span className="artifact-meta" data-test-id={`artifact-version-${kind}`}>
          Version {artifact.version}
          {artifact.streaming ? ' — arriving' : ''}
        </span>
      </header>

      {/*
        Staleness names what moved above it, because "needs attention" on its own tells the user
        there is a problem without telling them whose. Each window comes from the two upstream
        versions the store recorded, which is the same pair the agent diffs to propose a fix.

        One line per window, because an artifact can be waiting on more than one thing at once — the
        audience sits below both the research questions and the guide — and each is resolved on its
        own. Naming only the most recent would tell the user their study needs less attention than it
        does.
      */}
      {stalenessWindows.map((window) => (
        <p
          className="artifact-stale"
          data-test-id={`artifact-stale-${kind}-${window.upstream_kind}`}
          key={window.upstream_kind}
        >
          {staleApprovalText(kind, staleReasonOf([window]))}
        </p>
      ))}

      <div className="artifact-approval">
        <button
          type="button"
          data-test-id={`artifact-approve-${kind}`}
          disabled={!approval.can || busy}
          onClick={onApprove}
        >
          Approve
        </button>
        {!approval.can && (
          <span className="artifact-meta" data-test-id={`artifact-approve-reason-${kind}`}>
            {approval.message}
          </span>
        )}
      </div>

      {notice !== null && (
        <p className="artifact-notice" data-test-id={`artifact-notice-${kind}`} role="status">
          {notice}
        </p>
      )}

      <ArtifactBody
        kind={kind}
        content={content}
        contentUnreadable={contentUnreadable}
        version={contentVersion}
        audienceCatalog={audienceCatalog}
        // Editing is closed while the document is still arriving: the agent is mid-write, so a
        // save now would put a half-received body over the version it is still producing. The
        // approve button is withheld for the same reason, and for the same length of time.
        disabled={busy || locked || artifact.streaming}
        onSaveDocument={onSaveDocument}
        onRewordQuestion={onRewordQuestion}
      />

      <button
        type="button"
        data-test-id={`artifact-history-toggle-${kind}`}
        onClick={onToggleHistory}
      >
        {historyOpen ? 'Hide history' : 'Version history'}
      </button>

      {historyOpen && (
        <ol className="artifact-history" data-test-id={`artifact-history-${kind}`}>
          {history === undefined ? (
            <li>Loading…</li>
          ) : (
            history.map((version) => (
              <li
                key={version.version}
                data-test-id={`artifact-history-${kind}-${version.version}`}
              >
                Version {version.version} — {version.authoredBy === 'agent' ? 'Sigora' : 'you'}
                {version.version === artifact.approvedVersion ? ' — approved' : ''}
              </li>
            ))
          )}
        </ol>
      )}
    </article>
  );
};
